/**
 * Capacity model rows — entity-configured list (§1E).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart2, Loader2, Pencil, Trash2, Check, X, Gauge, Users } from 'lucide-react';
import type { FacetDefinition, SortOption, TableColumnWithRender } from '../../../components/collection/types';
import {
  createCapacityRow,
  createPriorityCapacityRow,
  deleteCapacityRow,
  deletePriorityCapacityRow,
  getCapacityRows,
  getPriorityCapacityRows,
  getScenarios,
  updateCapacityRow,
  updatePriorityCapacityRow,
  updateScenario,
} from '../../../lib/planningEngineService';
import { getRosterSupply } from '../../../lib/planningRosterService';
import {
  deriveRosterCapacityRows,
  RESOURCE_KIND_LABELS,
  scenarioUsesRoster,
  type RosterContribution,
} from '../../../lib/planningRoster';
import { useResourceRosterEnabled } from '../../../hooks/useResourceRosterEnabled';
import { usePlanningEngine } from './PlanningEngineContext';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import type { CapacityModelRow, PlanningMode, Scenario } from '../../../lib/planningEngineModule.types';
import { exportCsv } from './planningEngineViewShared';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';

/**
 * Doc 102 §5.5: Priority Estimate mode and Work Units mode read separate
 * capacity tables (planning_priority_capacity / planning_capacity). The
 * engine already reads the right one per scenario — this view previously
 * only ever showed/edited planning_capacity, so a Priority Estimate scenario's
 * actual capacity model was invisible here no matter what you added.
 */
type CapacityModel = 'priority_estimate' | 'work_units';

type UnifiedCapacityRow = CapacityModelRow & {
  capacityModel: CapacityModel;
  /** Roster-derived rows carry the people behind the number (PRD Phase A). */
  contributors?: RosterContribution[];
};

/** Roster rows are computed at run time and have no stored identity to edit. */
function isDerived(row: UnifiedCapacityRow): boolean {
  return row.source === 'roster';
}

const CAPACITY_MODEL_LABELS: Record<CapacityModel, string> = {
  priority_estimate: 'Priority Estimate',
  work_units: 'Work Units',
};

/** Hybrid scenarios use both tables (doc 102 §5.3) — default new rows to
 * Priority Estimate there since undecomposed priorities fall back to it. */
function defaultCapacityModelForMode(mode: PlanningMode | undefined): CapacityModel {
  return mode === 'work_units' ? 'work_units' : 'priority_estimate';
}

function CapacityDetailPanel({
  row,
  scenario,
  onUpdated,
  onDeleted,
}: {
  row: UnifiedCapacityRow;
  scenario?: Scenario;
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resourceTypes = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  useEffect(() => {
    setDraft(row);
    setEditing(false);
  }, [row]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (row.capacityModel === 'priority_estimate') {
        await updatePriorityCapacityRow(row.id, draft);
      } else {
        await updateCapacityRow(row.id, draft);
      }
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this capacity row?')) return;
    setSaving(true);
    try {
      if (row.capacityModel === 'priority_estimate') {
        await deletePriorityCapacityRow(row.id);
      } else {
        await deleteCapacityRow(row.id);
      }
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // A roster-derived pool is not a row anyone typed — it is a roll-up. Showing
  // it read-only with the people behind the number is the first time
  // PlanningPeopleCapacity data is visible where the plan is decided.
  if (isDerived(row)) {
    return (
      <div className="p-6 space-y-5 h-full overflow-auto">
        <div>
          <h3 className="text-lg font-semibold">{row.resourceType}</h3>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Derived from the roster · {CAPACITY_MODEL_LABELS[row.capacityModel]}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-[var(--text-ter)]">Scenario</dt><dd>{scenario?.name ?? row.scenarioId}</dd></div>
          <div><dt className="text-[var(--text-ter)]">Combined weekly rate</dt><dd className="tabular-nums">{row.weeklyCapacityHours} h/wk</dd></div>
          <div><dt className="text-[var(--text-ter)]">Effective hours</dt><dd className="tabular-nums">{row.effectiveCapacityHours.toFixed(1)}</dd></div>
          <div><dt className="text-[var(--text-ter)]">WU capacity</dt><dd className="tabular-nums font-medium">{row.workUnitCapacity.toFixed(1)}</dd></div>
        </dl>
        <div>
          <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider mb-2">
            Who is behind this number
          </p>
          <div className="border border-[var(--rule)] divide-y divide-[var(--rule)]">
            {(row.contributors ?? []).map((c) => (
              <div key={c.assignmentId} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="min-w-0 truncate">
                  {c.displayName}
                  <span className="ml-2 text-xs text-[var(--text-ter)]">{RESOURCE_KIND_LABELS[c.kind]}</span>
                </span>
                <span className="text-xs text-[var(--text-ter)] tabular-nums shrink-0 ml-3">
                  {c.hoursPerWeek} h/wk × {c.effectiveWeeks.toFixed(1)} wks = {c.grossHours.toFixed(0)} h
                </span>
              </div>
            ))}
            {(row.contributors ?? []).length === 0 && (
              <p className="px-3 py-3 text-sm text-[var(--text-ter)]">No contributors resolved.</p>
            )}
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-2">
            Weeks are the part of this scenario's horizon each assignment is effective for; the
            capacity buffer is applied to the total. Change hours in{' '}
            <Link to="/planning/people-capacity" className="text-primary-600 hover:underline">
              People &amp; Capacity → Roles
            </Link>
            , then re-run.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 h-full overflow-auto">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold">
          {row.resourceType}
          {row.resourceName ? ` · ${row.resourceName}` : ''}
        </h3>
        <div className="flex items-center gap-1">
          {!editing && (
            <button type="button" onClick={() => setEditing(true)} className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={() => void remove()} disabled={saving} className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {error && <div className="text-sm text-[var(--coral)]">{error}</div>}
      {editing ? (
        <div className="space-y-3">
          <select className="w-full text-sm border rounded px-2 py-1.5" value={draft.resourceType} onChange={(e) => setDraft({ ...draft, resourceType: e.target.value })}>
            {resourceTypes.map((rt) => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
          </select>
          <input className="w-full text-sm border rounded px-2 py-1.5" placeholder="Team / resource name" value={draft.resourceName ?? ''} onChange={(e) => setDraft({ ...draft, resourceName: e.target.value || null })} />
          <input type="number" min={0} step={0.5} className="w-full text-sm border rounded px-2 py-1.5" value={draft.weeklyCapacityHours} onChange={(e) => setDraft({ ...draft, weeklyCapacityHours: Number(e.target.value) })} />
          <div className="flex gap-2">
            <button type="button" onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[var(--slate)] text-white">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Save
            </button>
            <button type="button" onClick={() => { setDraft(row); setEditing(false); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border">
              <X className="h-3 w-3" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-[var(--text-ter)]">Scenario</dt><dd>{scenario?.name ?? row.scenarioId}</dd></div>
          <div><dt className="text-[var(--text-ter)]">Capacity model</dt><dd>{CAPACITY_MODEL_LABELS[row.capacityModel]}</dd></div>
          <div><dt className="text-[var(--text-ter)]">Weekly hours</dt><dd className="tabular-nums">{row.weeklyCapacityHours}</dd></div>
          <div><dt className="text-[var(--text-ter)]">Effective hours</dt><dd className="tabular-nums">{row.effectiveCapacityHours.toFixed(1)}</dd></div>
          <div><dt className="text-[var(--text-ter)]">WU capacity</dt><dd className="tabular-nums font-medium">{row.workUnitCapacity.toFixed(1)}</dd></div>
        </dl>
      )}
    </div>
  );
}

// ─── Create modal ────────────────────────────────────────────────────────────

function CreateCapacityRowModal({
  projectId,
  scenarios,
  defaultScenarioId,
  onClose,
  onCreated,
}: {
  projectId: string;
  scenarios: Scenario[];
  defaultScenarioId: string;
  onClose: () => void;
  onCreated: (scenarioId: string) => void;
}) {
  const scenarioById = useMemo(() => new Map(scenarios.map((s) => [s.id, s])), [scenarios]);
  const [scenarioId, setScenarioId] = useState(defaultScenarioId || scenarios[0]?.id || '');
  const [capacityModel, setCapacityModel] = useState<CapacityModel>(() =>
    defaultCapacityModelForMode(scenarioById.get(defaultScenarioId || scenarios[0]?.id || '')?.planningMode),
  );
  const [resourceType, setResourceType] = useState('engineering');
  const [resourceName, setResourceName] = useState('');
  const [weeklyHours, setWeeklyHours] = useState(40);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resourceTypes = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  const handleSave = async () => {
    if (!scenarioId || !resourceType) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { scenarioId, resourceType, resourceName: resourceName.trim() || null, weeklyCapacityHours: weeklyHours };
      if (capacityModel === 'priority_estimate') {
        await createPriorityCapacityRow(projectId, payload);
      } else {
        await createCapacityRow(projectId, payload);
      }
      onCreated(scenarioId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-[var(--text-ter)]" />
            <h3 className="text-base font-semibold text-[var(--text-pri)]">Add capacity row</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Scenario <span className="text-[var(--coral)]">*</span></label>
            <select
              value={scenarioId}
              onChange={(e) => {
                setScenarioId(e.target.value);
                setCapacityModel(defaultCapacityModelForMode(scenarioById.get(e.target.value)?.planningMode));
              }}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— select —</option>
              {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Capacity model <span className="text-[var(--coral)]">*</span></label>
            <select value={capacityModel} onChange={(e) => setCapacityModel(e.target.value as CapacityModel)} className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="priority_estimate">Priority Estimate (planning_priority_capacity)</option>
              <option value="work_units">Work Units (planning_capacity)</option>
            </select>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              {scenarioById.get(scenarioId)?.planningMode === 'hybrid_remaining'
                ? 'This scenario is Hybrid — it can use both. Priority Estimate covers undecomposed priorities.'
                : 'Matches the selected scenario’s planning mode by default.'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Resource type <span className="text-[var(--coral)]">*</span></label>
              <select value={resourceType} onChange={(e) => setResourceType(e.target.value)} className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                {resourceTypes.map((rt) => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Weekly capacity (hrs)</label>
              <input type="number" min={0} step={0.5} value={weeklyHours} onChange={(e) => setWeeklyHours(Number(e.target.value))} className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Team / resource name <span className="text-[var(--text-ter)] font-normal">(optional)</span></label>
            <input type="text" value={resourceName} onChange={(e) => setResourceName(e.target.value)} placeholder="e.g. Platform team" className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">Cancel</button>
          <button type="button" onClick={() => void handleSave()} disabled={saving || !scenarioId} className="px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Add row
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlanningCapacityView() {
  const { projectId, activeScenario, scenarios } = usePlanningEngine();
  const [rows, setRows] = useState<UnifiedCapacityRow[]>([]);
  const [localScenarios, setLocalScenarios] = useState<Scenario[]>(scenarios);
  const [scenarioFilter, setScenarioFilter] = useState<string>(activeScenario?.id ?? '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [savingSource, setSavingSource] = useState(false);
  const rosterEnabled = useResourceRosterEnabled();
  const enabledResourceTypes = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  const CAPACITY_FACETS = useMemo<FacetDefinition<string>[]>(() => [
    {
      key: 'resourceType',
      label: 'Resource type',
      options: enabledResourceTypes.map((rt) => ({ value: rt.key, label: rt.label })),
    },
    {
      key: 'capacityModel',
      label: 'Capacity model',
      options: [
        { value: 'priority_estimate', label: CAPACITY_MODEL_LABELS.priority_estimate },
        { value: 'work_units', label: CAPACITY_MODEL_LABELS.work_units },
      ],
    },
  ], [enabledResourceTypes]);

  const scenarioById = useMemo(() => new Map(localScenarios.map((s) => [s.id, s])), [localScenarios]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [priorityRows, workUnitRows, s] = await Promise.all([
        getPriorityCapacityRows(projectId, scenarioFilter || undefined),
        getCapacityRows(projectId, scenarioFilter || undefined),
        getScenarios(projectId),
      ]);
      setLocalScenarios(s);

      // Roster-derived pools are computed, not stored — so they are assembled
      // here rather than fetched, and only for scenarios that opted in. Shown
      // alongside manual rows: roster mode adds supply, it never hides what
      // someone typed in.
      const derived: UnifiedCapacityRow[] = [];
      const rosterScenarios = s.filter(
        (sc) => scenarioUsesRoster(sc) && (!scenarioFilter || sc.id === scenarioFilter),
      );
      if (rosterScenarios.length > 0) {
        const { resources, assignments } = await getRosterSupply(projectId);
        for (const sc of rosterScenarios) {
          const pools = deriveRosterCapacityRows({ scenario: sc, resources, assignments });
          const model: CapacityModel = defaultCapacityModelForMode(sc.planningMode);
          derived.push(...pools.map((p) => ({ ...p, capacityModel: model })));
        }
      }

      setRows([
        ...priorityRows.map((r) => ({ ...r, capacityModel: 'priority_estimate' as const })),
        ...workUnitRows.map((r) => ({ ...r, capacityModel: 'work_units' as const })),
        ...derived,
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, scenarioFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (activeScenario?.id) {
      setScenarioFilter(activeScenario.id);
    }
  }, [activeScenario?.id]);


  const sortOptions: SortOption<'resourceType' | 'weeklyCapacityHours'>[] = [
    { field: 'resourceType', label: 'Resource', defaultDirection: 'asc' },
    { field: 'weeklyCapacityHours', label: 'Weekly hrs', defaultDirection: 'desc' },
  ];

  const tableColumns: TableColumnWithRender<UnifiedCapacityRow>[] = useMemo(
    () => [
      { key: 'scenario', label: 'Scenario', minWidth: 120, flex: true, render: (r) => scenarioById.get(r.scenarioId)?.name ?? r.scenarioId },
      {
        key: 'capacityModel',
        label: 'Model',
        sortable: true,
        minWidth: 130,
        render: (r) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
              r.capacityModel === 'priority_estimate'
                ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]'
                : 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]'
            }`}
          >
            {CAPACITY_MODEL_LABELS[r.capacityModel]}
          </span>
        ),
      },
      { key: 'resourceType', label: 'Type', sortable: true, minWidth: 100, render: (r) => r.resourceType },
      {
        key: 'source',
        label: 'Source',
        minWidth: 90,
        render: (r) =>
          isDerived(r) ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)]">
              <Users className="h-3 w-3" />
              Roster
            </span>
          ) : (
            <span className="text-xs text-[var(--text-ter)]">Manual</span>
          ),
      },
      { key: 'name', label: 'Name', minWidth: 160, render: (r) => r.resourceName ?? '—' },
      { key: 'weekly', label: 'Weekly', align: 'right', sortable: true, minWidth: 72, render: (r) => <span className="tabular-nums">{r.weeklyCapacityHours}</span> },
      { key: 'wu', label: 'WU cap', align: 'right', minWidth: 72, render: (r) => <span className="tabular-nums font-medium">{r.workUnitCapacity.toFixed(1)}</span> },
    ],
    [scenarioById],
  );

  const filteredScenario = scenarioFilter ? scenarioById.get(scenarioFilter) : undefined;

  const toggleCapacitySource = async () => {
    if (!filteredScenario) return;
    const next = scenarioUsesRoster(filteredScenario) ? 'manual' : 'roster';
    setSavingSource(true);
    try {
      await updateScenario(filteredScenario.id, { capacitySource: next });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingSource(false);
    }
  };

  const scenarioFilterBar = (
    <div className="px-6 py-2 border-b border-[var(--rule)] bg-[var(--bg-raised)] flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-[var(--text-ter)]">Scenario filter</span>
        <select
          className="text-sm border px-3 py-1.5"
          value={scenarioFilter}
          onChange={(e) => setScenarioFilter(e.target.value)}
        >
          <option value="">All scenarios</option>
          {localScenarios.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Where this scenario's pools come from. Anonymous pools stay a fully
          supported mode — the correct fidelity for an early viability pass. */}
      {rosterEnabled && filteredScenario && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--text-ter)]">Capacity source</span>
          <button
            type="button"
            onClick={() => void toggleCapacitySource()}
            disabled={savingSource}
            title={
              scenarioUsesRoster(filteredScenario)
                ? 'Pools are rolled up from active role assignments, alongside any manual rows. Switch back to plan against anonymous pools only.'
                : 'Pools come only from the manual rows below. Switch to roll up the roster — the people, hours and effective windows already entered in People & Capacity.'
            }
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              scenarioUsesRoster(filteredScenario)
                ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)] hover:bg-[var(--green-dim)]'
                : 'bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)] hover:bg-[var(--hover-tint)]'
            }`}
          >
            {savingSource ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users className="h-3 w-3" />}
            {scenarioUsesRoster(filteredScenario) ? 'Roster' : 'Manual pools'}
          </button>
          {scenarioUsesRoster(filteredScenario) && (
            <Link to="/planning/people-capacity" className="text-xs text-primary-600 hover:underline">
              Edit roles →
            </Link>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
    <div className="mx-4 md:mx-6 mt-4 mb-4 border border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--mustard-dim)]/50 dark:bg-[var(--mustard)]/10 px-4 py-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
      <p className="font-medium">Viability Resource Budget — not Launch Calendar supply</p>
      <p className="mt-1 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        These rows feed the achievability / above-below-line engine. To change shared-team hours
        that move the Launch Calendar Gantt, edit{' '}
        <Link
          to="/planning/engine/time-phased?tab=supply"
          className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
        >
          Time-Phased → Supply calendar
        </Link>
        .
      </p>
    </div>
    <PlanningEntityListPage
      items={rows}
      loading={loading}
      getId={(r) => r.id}
      title="Resource Budget"
      subtitle="Viability capacity (WU) per resource type — not Launch Calendar weekly supply"
      icon={<BarChart2 className="h-4 w-4 text-[var(--text-ter)]" />}
      searchFields={['resourceType', 'resourceName']}
      sortOptions={sortOptions}
      defaultSort={{ field: 'resourceType', direction: 'asc' }}
      groupOptions={[{ field: 'resourceType', label: 'Resource type' }]}
      facets={CAPACITY_FACETS}
      tableColumns={tableColumns}
      renderCard={(r, { selected }) => (
        <div className="p-4">
          <p className="font-medium">{r.resourceType}</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            {CAPACITY_MODEL_LABELS[r.capacityModel]} · {r.weeklyCapacityHours}h/wk · {r.workUnitCapacity.toFixed(1)} WU
          </p>
          {selected && <p className="text-xs text-[var(--text-ter)] mt-2">Selected</p>}
        </div>
      )}
      renderDetail={(r) => (
        <CapacityDetailPanel
          row={r}
          scenario={scenarioById.get(r.scenarioId)}
          onUpdated={() => void load()}
          onDeleted={() => { setSelectedId(null); void load(); }}
        />
      )}
      selectedId={selectedId}
      onSelectionChange={setSelectedId}
      urlParamName="capacity"
      onRefresh={() => void load()}
      onExport={() => exportCsv(`planning-capacity-${new Date().toISOString().slice(0, 10)}.csv`, ['scenario_id', 'capacity_model', 'resource_type', 'resource_name', 'weekly_hours', 'wu_capacity'], rows.map((r) => [r.scenarioId, r.capacityModel, r.resourceType, r.resourceName ?? '', String(r.weeklyCapacityHours), r.workUnitCapacity.toFixed(1)]))}
      onCreate={() => setCreating(true)}
      createLabel="Add row"
      emptyTitle="No capacity rows"
      emptyDescription="Add capacity rows for each resource type in your scenario."
      emptyAction={{ label: 'Add row', onClick: () => setCreating(true) }}
      error={error}
      activeScenario={activeScenario}
      itemLabel="row"
      headerExtra={scenarioFilterBar}
    />
    {creating && projectId && (
      <CreateCapacityRowModal
        projectId={projectId}
        scenarios={localScenarios}
        defaultScenarioId={scenarioFilter || activeScenario?.id || ''}
        onClose={() => setCreating(false)}
        onCreated={(newScenarioId) => {
          setCreating(false);
          // The list is filtered server-side by scenarioFilter — a row created
          // for a different scenario than the current filter would otherwise
          // save successfully but never appear, with no indication why.
          if (newScenarioId !== scenarioFilter) {
            setScenarioFilter(newScenarioId);
          } else {
            void load();
          }
        }}
      />
    )}
    </>
  );
}
